import { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import axios from "axios";
import { format, eachDayOfInterval, startOfMonth, endOfMonth } from 'date-fns';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const API_BASE_URL = "https://api.muktihospital.com/api";

const PharmacyPerformanceChart = () => {
    const [chartData, setChartData] = useState({ series: [], options: {} });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchChartData = async () => {
            setLoading(true);
            const now = new Date();
            const firstDay = startOfMonth(now);
            const lastDay = endOfMonth(now);
            const daysOfMonth = eachDayOfInterval({ start: firstDay, end: lastDay });

            const labels = daysOfMonth.map(day => format(day, 'd'));
            const dailySales = Array(daysOfMonth.length).fill(0);
            const dailyPurchases = Array(daysOfMonth.length).fill(0);

            try {
                const api = axios.create({ baseURL: API_BASE_URL, headers: { "x-api-key": API_KEY } });

                const promises = daysOfMonth.map(async (day, index) => {
                    const dateStr = format(day, 'yyyy-MM-dd');
                    
                    const salesPromise = api.get(`/medicine-bill/bills/range?startDate=${dateStr}&endDate=${dateStr}`)
                        .then(res => parseFloat(res.data.totalAmount || 0))
                        .catch(() => 0);
                        
                    // NOTE: We fetch all purchases and filter, as there's no daily purchase API
                    const purchasePromise = api.get(`/medicine-purchase/all`)
                        .then(res => {
                            const allPurchases = res.data?.purchases || [];
                            const todayPurchases = allPurchases.filter(p => p.purchaseDate && p.purchaseDate.startsWith(dateStr));
                            return todayPurchases.reduce((sum, p) => sum + (p.netAmount || 0), 0);
                        })
                        .catch(() => 0);

                    const [sales, purchases] = await Promise.all([salesPromise, purchasePromise]);
                    dailySales[index] = sales;
                    dailyPurchases[index] = purchases;
                });

                await Promise.all(promises);

                setChartData({
                    series: [
                        { name: 'Sales', type: 'column', data: dailySales },
                        { name: 'Purchases', type: 'line', data: dailyPurchases }
                    ],
                    options: {
                        chart: { height: 400, type: 'line', toolbar: { show: false } },
                        stroke: { width: [0, 4], curve: 'smooth' },
                        plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
                        dataLabels: { enabled: false },
                        labels: labels,
                        yaxis: { labels: { formatter: (val) => `৳${(val / 1000).toFixed(0)}k` } },
                        colors: ["#6AC75A", "#465DFF"],
                        tooltip: { y: { formatter: (val) => `৳${Math.round(val).toLocaleString()}` } }
                    },
                });

            } catch (error) {
                console.error("Error fetching chart data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchChartData();
    }, []);

    if (loading) {
        return <Skeleton height={400} />;
    }

    return (
        <div>
            <ReactApexChart
                options={chartData.options}
                series={chartData.series}
                type="line"
                height={400}
            />
        </div>
    );
};

export default PharmacyPerformanceChart;